import { Injectable, signal, computed, effect, Signal, Injector, inject, runInInjectionContext } from '@angular/core';

export type SignalListener<T = any> = (payload: T) => void;
export interface Unsubscribe {
  (): void;
}

@Injectable({
  providedIn: 'root'
})
export class StoreStateServiceService {
  private injector = inject(Injector);
  private dataStore = new Map<string, Signal<any>>();
  
  searchText = signal('');
  // Store for event listeners
  private eventListeners = new Map<string, Set<{ listener: SignalListener }>>();
  /**
   * Create or update a signal with a given key
   */
  createSignal<T>(key: string, initialValue: T) {
    if (!this.dataStore.has(key)) {
      const newSignal = signal<T>(initialValue);
      this.dataStore.set(key, newSignal);
      return newSignal;
    }
    return this.dataStore.get(key) as Signal<T>;
  }

  /**
   * Get an existing signal by key
   */
  getSignal<T>(key: string): Signal<T> | undefined {
    return this.dataStore.get(key) as Signal<T> | undefined;
  }

  /**
   * Set or update a signal value
   */
  set<T>(key: string, value: T): void {
    const dataSignal = this.getSignal<T>(key);
    if (dataSignal && 'set' in dataSignal) {
      (dataSignal as { set(value: T): void }).set(value);
      this.emit(`${key}:changed`, value);
    } else {
      const newSignal = this.createSignal<T>(key, value);
      this.emit(`${key}:created`, value);
    }
  }

  /**
   * Update a specific property of an object in a signal
   */
update<T>(key: string, propertyPathOrValue: string | T, value?: unknown): void {
  const dataSignal = this.getSignal<T>(key);
  if (!dataSignal || !('update' in dataSignal)) return;
  const signal = dataSignal as { update(updaterFn: (value: T) => T): void };
  if (typeof propertyPathOrValue === 'string' && value !== undefined) {
    const path = propertyPathOrValue.split('.');
    signal.update((currentValue: any) => {
      if (!currentValue || typeof currentValue !== 'object') return currentValue;
      const updated = { ...currentValue };
      let temp = updated;
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        temp[key] = { ...temp[key] };
        temp = temp[key];
      }
      temp[path[path.length - 1]] = value;
      return updated;
    });
    this.emit(`${key}.${propertyPathOrValue}:updated`, value);
  } 
  else {
    const newValue = propertyPathOrValue as T;
    signal.update(() => newValue);
    this.emit(`${key}:updated`, newValue);
  }
}
  /**
   * Get the current value of a signal
   */
  getValue<T>(key: string): T | undefined {
    const dataSignal = this.getSignal<T>(key);
    return dataSignal ? dataSignal() : undefined;
  }

  /**
   * Create a computed signal that depends on other signals
   */
  compute<T, D>(key: string, computeFn: () => T): Signal<T> {
    const computedSignal = computed(computeFn);
    this.dataStore.set(key, computedSignal);
    return computedSignal;
  }

  /**
   * Register an effect that reacts to signal changes
   * Fixed to use runInInjectionContext
   */
  registerEffect(effectFn: () => void): void {
    runInInjectionContext(this.injector, () => {
      effect(effectFn);
    });
  }

  /**
   * Add an event listener
   */
on<T>(eventType: string, listener: SignalListener<T>): () => void {
  if (!this.eventListeners.has(eventType)) {
    this.eventListeners.set(eventType, new Set());
  }
  const wrapper = { listener: listener as SignalListener };
  this.eventListeners.get(eventType)?.add(wrapper);
  return () => {
    this.eventListeners.get(eventType)?.delete(wrapper);
    if (this.eventListeners.get(eventType)?.size === 0) {
      this.eventListeners.delete(eventType);
      }
      };
}
  /**
   * Emit an event to all registered listeners
   */
  emit<T>(eventType: string, payload: T): void {
    if (this.eventListeners.has(eventType)) {
      this.eventListeners.get(eventType)?.forEach(listener => {
        listener.listener(payload);
      });
    }
  }

  /**
   * Clear a signal
   */
  clear(key: string): void {
    const dataSignal = this.getSignal(key);
    
    if (dataSignal && 'set' in dataSignal) {
      (dataSignal as { set(value: any): void }).set(null);
      this.emit(`${key}:cleared`, null);
    }
  }

  /**
   * Clear all signals
   */
  clearAll(): void {
    this.dataStore.forEach((signalObj, key) => {
      if ('set' in signalObj) {
        (signalObj as { set(value: any): void }).set(null);
        this.emit(`${key}:cleared`, null);
      }
    });
  }
}